
import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import GammaEmbed from "@/components/GammaEmbed";
import ChecklistDownload from "@/components/checklist/ChecklistDownload";
import ResourceCard from "@/components/insights/ResourceCard";
import { getInsightResources } from "@/services/insightResourceService";
import type { InsightResource } from "@/services/insightResourceService";
import { toast } from "@/hooks/use-toast";

const InsightsPage: React.FC = () => {
  const [resources, setResources] = useState<InsightResource[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const data = await getInsightResources();
        setResources(data);
      } catch (error) {
        console.error("Error fetching resources:", error);
        toast({
          title: "Error",
          description: "Failed to load resources",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  return (
    <Layout>
      <div className="container-custom py-16">
        <div className="max-w-4xl mx-auto mb-12 text-center">
          <h1 className="text-4xl font-bold text-techne-primaryPurple mb-4">Board Governance Insights</h1>
          <p className="text-xl text-gray-600">
            Access exclusive research, white papers, and tools on AI governance for board members
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <GammaEmbed />
          <ChecklistDownload />
        </div>

        {resources.length > 0 && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-techne-primaryPurple mb-6">Additional Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.map((resource) => (
                <ResourceCard
                  key={resource.id}
                  title={resource.title}
                  description={resource.description || undefined}
                  imageUrl={resource.image_url || undefined}
                  filePath={resource.file_path}
                  isGated={resource.is_gated}
                  resourceType={resource.resource_type}
                />
              ))}
            </div>
          </div>
        )}

        <div className="bg-gray-50 p-8 rounded-xl shadow-sm border border-gray-100 mb-12">
          <h2 className="text-2xl font-bold text-techne-primaryPurple mb-4">Stay Updated</h2>
          <p className="text-gray-600 mb-4">
            We're preparing additional insights and research papers on board-level AI governance. 
            Subscribe to our newsletter to be notified when new resources become available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <input
              type="email"
              placeholder="Your email address"
              className="px-4 py-2 border border-gray-300 rounded-md w-full sm:w-64"
            />
            <button
              className="bg-techne-primaryPurple text-white px-6 py-2 rounded-md hover:bg-techne-deepPurple transition-colors duration-300 w-full sm:w-auto"
            >
              Subscribe
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default InsightsPage;
